import { useRef, useEffect, useState } from "react";
import { toast } from "sonner";
import { XIcon, DownloadSimpleIcon, CopyIcon, XLogoIcon, TelegramLogoIcon, SpinnerGapIcon } from "@phosphor-icons/react";
import { stampBrandApe } from "../lib/shareLogo";

const NEON = "#00E599", DANGER = "#FF3366", GOLD = "#F0C000", MUTED = "#8b8b8b";
const shorten = (a) => (a && a.length > 12 ? `${a.slice(0, 4)}…${a.slice(-4)}` : a || "");

export const DevShareCard = ({ status, name, addresses = [], rugCount = 0, open, onClose }) => {
  const canvasRef = useRef(null);
  const [copying, setCopying] = useState(false);
  const isAlpha = status === "alpha";
  const accent = isAlpha ? GOLD : DANGER;

  const shareUrl = "https://alphaapeing.com/devs";
  const shareText = isAlpha
    ? `⭐ ${name} is a tracked ALPHA DEV on AlphaApeing — watching every wallet they launch from. 🦍`
    : `💀 ${name} is BLOCKED on AlphaApeing${rugCount > 0 ? ` — ${rugCount} rug${rugCount > 1 ? "s" : ""} on record` : ""}. DYOR. 🦍`;

  useEffect(() => {
    if (!open || !canvasRef.current) return;
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const W = 1080, H = 1080;
    ctx.fillStyle = "#050505";
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "rgba(255,255,255,0.035)";
    ctx.lineWidth = 1;
    for (let x = 0; x <= W; x += 60) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
    for (let y = 0; y <= H; y += 60) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }
    // tinted status glow strip
    ctx.fillStyle = isAlpha ? "rgba(240,192,0,0.06)" : "rgba(255,51,102,0.06)";
    ctx.fillRect(24, 24, W - 48, 250);
    ctx.strokeStyle = isAlpha ? "rgba(240,192,0,0.35)" : "rgba(255,51,102,0.35)";
    ctx.lineWidth = 4;
    ctx.strokeRect(24, 24, W - 48, H - 48);

    // wordmark
    ctx.textBaseline = "alphabetic";
    ctx.textAlign = "left";
    ctx.font = "800 46px Arial, sans-serif";
    ctx.fillStyle = "#fff";
    ctx.fillText("ALPHA", 70, 120);
    const w1 = ctx.measureText("ALPHA").width;
    ctx.fillStyle = NEON;
    ctx.fillText("APEING", 70 + w1, 120);
    ctx.font = "600 20px monospace";
    ctx.fillStyle = NEON;
    ctx.fillText("SOLANA MEME-COIN INTELLIGENCE", 72, 152);

    // status emblem + verdict
    ctx.font = "800 130px Arial, sans-serif";
    ctx.fillText(isAlpha ? "⭐" : "💀", 70, 400);
    ctx.fillStyle = accent;
    ctx.font = "800 96px Arial, sans-serif";
    ctx.fillText(isAlpha ? "ALPHA DEV" : "BLOCKED", 240, 385);

    // dev name
    ctx.fillStyle = "#fff";
    ctx.font = "800 84px Arial, sans-serif";
    ctx.fillText((name || "Unknown dev").slice(0, 20), 70, 560);

    // subtitle
    ctx.fillStyle = accent;
    ctx.font = "700 34px monospace";
    const sub = isAlpha
      ? `${addresses.length} wallet${addresses.length === 1 ? "" : "s"} tracked`
      : rugCount > 0
      ? `⚠ ${rugCount} rug${rugCount > 1 ? "s" : ""} on record`
      : "Flagged repeat rugger";
    ctx.fillText(sub, 70, 620);

    // wallets
    ctx.fillStyle = MUTED;
    ctx.font = "600 24px monospace";
    ctx.fillText("WALLETS ON RECORD", 70, 710);
    let cx = 70, cy = 740;
    ctx.font = "600 30px monospace";
    (addresses.slice(0, 8)).forEach((a) => {
      const label = shorten(a);
      const tw = ctx.measureText(label).width + 44;
      if (cx + tw > W - 70) { cx = 70; cy += 74; }
      ctx.strokeStyle = "rgba(255,255,255,0.14)";
      ctx.lineWidth = 2;
      ctx.strokeRect(cx, cy, tw, 56);
      ctx.fillStyle = "#cfcfcf";
      ctx.fillText(label, cx + 22, cy + 38);
      cx += tw + 18;
    });
    if (addresses.length === 0) {
      ctx.fillStyle = MUTED;
      ctx.font = "400 26px Arial, sans-serif";
      ctx.fillText("Roster building — grabbed from their coins", 70, 780);
    }

    // footer
    ctx.fillStyle = NEON;
    ctx.font = "700 30px monospace";
    ctx.fillText("alphaapeing.com", 70, 1015);
    ctx.fillStyle = MUTED;
    ctx.font = "400 22px Arial, sans-serif";
    ctx.textAlign = "right";
    ctx.fillText("Research tool · not financial advice", W - 70, 1015);
    ctx.textAlign = "left";
    stampBrandApe(c);
  }, [open, status, name, addresses, rugCount, isAlpha, accent]);

  if (!open) return null;

  const getBlob = () => new Promise((res) => canvasRef.current.toBlob(res, "image/png"));

  const download = async () => {
    const blob = await getBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `alphaapeing-${isAlpha ? "alpha" : "blocked"}-${(name || "dev").replace(/\s+/g, "-")}.png`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Dev card downloaded");
  };

  const copy = async () => {
    setCopying(true);
    try {
      const blob = await getBlob();
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
      toast.success("Image copied — paste it into your post");
    } catch {
      toast.error("Copy not supported here — use Download instead");
    }
    setCopying(false);
  };

  const shareX = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, "_blank");
  const shareTg = () => window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`, "_blank");

  return (
    <div data-testid="dev-share-modal" className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-card border border-white/15 max-w-md w-full p-5 space-y-4 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-lg">Share {name} {isAlpha ? "Alpha" : "Blocked"} card</h3>
          <button data-testid="dev-share-close-btn" onClick={onClose} className="p-1.5 text-muted-foreground hover:text-white"><XIcon size={20} /></button>
        </div>

        <div className="border border-white/10 overflow-hidden">
          <canvas ref={canvasRef} width={1080} height={1080} className="w-full h-auto block" />
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button data-testid="dev-share-download-btn" onClick={download} className="flex items-center justify-center gap-2 px-4 py-2.5 border border-white/15 text-sm font-semibold hover:border-neon/40 hover:text-neon transition-colors">
            <DownloadSimpleIcon size={16} /> Download
          </button>
          <button data-testid="dev-share-copy-btn" onClick={copy} disabled={copying} className="flex items-center justify-center gap-2 px-4 py-2.5 border border-white/15 text-sm font-semibold hover:border-neon/40 hover:text-neon transition-colors disabled:opacity-50">
            {copying ? <SpinnerGapIcon size={16} className="animate-spin" /> : <CopyIcon size={16} />} Copy image
          </button>
          <button data-testid="dev-share-x-btn" onClick={shareX} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-white text-black text-sm font-semibold hover:bg-white/90 transition-colors">
            <XLogoIcon size={16} weight="fill" /> Share on X
          </button>
          <button data-testid="dev-share-tg-btn" onClick={shareTg} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-[#229ED9] text-white text-sm font-semibold hover:bg-[#229ED9]/90 transition-colors">
            <TelegramLogoIcon size={16} weight="fill" /> Telegram
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground text-center">Tip: hit "Copy image", then paste into your X or Telegram post for the full card.</p>
      </div>
    </div>
  );
};
